import React, { useState } from 'react';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';

import { InternalLogin } from '@components/Login/InternalLogin';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Dashboard } from './Dashboard';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DashboardSearch } from './DashboardSearch';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  & .MuiToggleButton-root {
    padding: 7px 50px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100px;
  width: calc(100% - 6rem);

  & .MuiOutlinedInput-root {
    width: 220px;
  }
`;

export const Admin: React.FC = () => {
  const [tab, setTab] = useState('login');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(4, 'week'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const [extendedStartDate, setExtendedStartDate] = useState<Dayjs | null>(dayjs().subtract(1, 'year'));
  const [extendedEndDate, setExtendedEndDate] = useState<Dayjs | null>(dayjs());

  const changeTab = (_: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
    if (!newAlignment) return;
    setTab(newAlignment);
  };

  return (
    <Container>
      <Header>
        <ToggleButtonGroup
          color='primary'
          size='small'
          value={tab}
          exclusive
          onChange={changeTab}
          aria-label='input type'
        >
          <ToggleButton value='login' aria-label='login'>
            Login
          </ToggleButton>
          <ToggleButton value='dashboard' aria-label='dashboard'>
            Dashboard
          </ToggleButton>
          <ToggleButton value='search' aria-label='search'>
            Search
          </ToggleButton>
        </ToggleButtonGroup>
        {(tab === 'dashboard' || tab === 'search') && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <DatePicker
                label='Start Date'
                value={tab === 'dashboard' ? startDate : extendedStartDate}
                format='LL'
                onChange={(newStartDate) => {
                  if (newStartDate === null || newStartDate === undefined) return;
                  if (tab === 'dashboard' ? newStartDate.isBefore(endDate) : newStartDate.isBefore(extendedEndDate)) {
                    tab === 'dashboard' ? setStartDate(newStartDate) : setExtendedStartDate(newStartDate);
                  }
                }}
              />
              <DatePicker
                label='End Date'
                value={tab === 'dashboard' ? endDate : extendedEndDate}
                format='LL'
                onChange={(newEndDate) => {
                  if (newEndDate === null || newEndDate === undefined) return;
                  const isAfterStart =
                    tab === 'dashboard' ? newEndDate.isAfter(startDate) : newEndDate.isAfter(extendedStartDate);

                  if (isAfterStart && newEndDate.isBefore(dayjs())) {
                    tab === 'dashboard' ? setEndDate(newEndDate) : setExtendedEndDate(newEndDate);
                  }
                }}
              />
            </Stack>
          </LocalizationProvider>
        )}
        {tab !== 'dashboard' && tab !== 'search' && <div />}
      </Header>
      {tab === 'login' && <InternalLogin />}
      {tab === 'dashboard' && <Dashboard startDate={startDate} endDate={endDate} />}
      {tab === 'search' && <DashboardSearch startDate={extendedStartDate} endDate={extendedEndDate} />}
    </Container>
  );
};
